import { memo, useState, useCallback, useEffect } from "react";
import { useI18n } from "@/i18n";
import { useSessionStore } from "@/application/stores/sessionStore";
import { useTenantUsage } from "@/hooks/useTenantUsage";
import { listUsers, listRoles, createUser, updateUser, updateUserRole, updateUserStatus } from "@/infrastructure/user/userService";
import type { UserRecord, RoleRecord } from "@/infrastructure/user/userService";
import { Pagination, Tooltip } from "@/design-system";
import { colors, typography, transitions } from "@/design-system/tokens";

const PAGE_SIZE = 8;

export const UsersPage = memo(function UsersPage() {
  const { t } = useI18n();
  const tenantId = useSessionStore(s => s.tenantId);
  const currentUsername = useSessionStore(s => s.username);
  const { isAtLimit, refresh: refreshUsage } = useTenantUsage();
  const userAtLimit = isAtLimit("users");
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const showToast = useCallback((message: string, type: "success" | "error") => { setToast({ message, type }); setTimeout(() => setToast(null), 3500); }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listUsers({ tenantId, page, limit: PAGE_SIZE });
      setUsers(res.data.filter(u => !(u.username === currentUsername && u.roleName === "SYSTEM_ADMIN")));
      setTotal(res.total > 0 ? res.total - 1 : 0);
    } catch {}
    setLoading(false);
  }, [tenantId, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleToggleStatus = useCallback(async (user: UserRecord) => {
    const newStatus = user.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await updateUserStatus(user.id, newStatus);
      showToast(t.users.successStatusChanged, "success");
      fetchUsers();
      refreshUsage();
    } catch {
      showToast(t.users.errorForbidden, "error");
    }
  }, [fetchUsers, showToast, t.users, refreshUsage]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "36px 36px 48px", minHeight: "100vh", background: "#fff", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h1 style={{ fontFamily: typography.fontFamily, fontSize: "1.9rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, letterSpacing: "-0.03em" }}>{t.users.title} ({total})</h1>
          <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#999", margin: "4px 0 0" }}>{t.users.subtitle}</p>
        </div>
        <Tooltip content={t.planLimit.tooltipDisabled} disabled={!userAtLimit}>
          <button disabled={userAtLimit} onClick={() => setShowCreate(true)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: userAtLimit ? "#ccc" : colors.primary, border: "none", borderRadius: "10px", padding: "10px 20px", cursor: userAtLimit ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "6px", transition: `background ${transitions.fast}`, opacity: userAtLimit ? 0.7 : 1 }} onMouseEnter={e => { if (!userAtLimit) (e.currentTarget as HTMLButtonElement).style.background = colors.primaryHover; }} onMouseLeave={e => { if (!userAtLimit) (e.currentTarget as HTMLButtonElement).style.background = colors.primary; }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
            {t.users.addUser}
          </button>
        </Tooltip>
      </div>

      <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e8e8e8", overflow: "hidden", minHeight: "300px" }}>
        {loading && <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px" }}><span style={{ width: "24px", height: "24px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /></div>}
        {!loading && users.length === 0 && <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "64px 24px", gap: "10px" }}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#bbb" }}>{t.users.emptyTitle}</span></div>}
        {!loading && users.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #f2f2f2" }}>
                  {[t.users.colName, t.users.colUsername, t.users.colEmail, t.users.colRole, t.users.colStatus, t.users.colActions].map(col => (
                    <th key={col} style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.bold, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.1em", padding: "14px 16px", textAlign: "left" }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} style={{ borderBottom: "1px solid #f7f7f7", transition: `background ${transitions.fast}` }} onMouseEnter={e => ((e.currentTarget as HTMLTableRowElement).style.background = "#fafafa")} onMouseLeave={e => ((e.currentTarget as HTMLTableRowElement).style.background = "transparent")}>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#111" }}>{user.name}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{user.username}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888" }}>{user.email}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.semibold, color: "#666", background: "#f0f0f0", padding: "3px 8px", borderRadius: "6px" }}>{user.roleName.replace(/_/g, " ")}</span></td>
                    <td style={cell}><StatusBadge status={user.status} /></td>
                    <td style={cell}>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <RowBtn label={t.users.editUser} onClick={() => setEditingUser(user)} />
                        <RowBtn label={user.status === "ACTIVE" ? t.users.deactivate : t.users.activate} onClick={() => handleToggleStatus(user)} danger={user.status === "ACTIVE"} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div style={{ padding: "0 16px 14px" }}>
                <Pagination currentPage={page} totalPages={totalPages} totalItems={total} onPageChange={setPage} />
              </div>
            )}
          </div>
        )}
      </div>

      {showCreate && <CreateUserModal tenantId={tenantId} onCreated={() => { setShowCreate(false); fetchUsers(); refreshUsage(); showToast(t.users.successCreated, "success"); }} onClose={() => setShowCreate(false)} onError={(msg) => showToast(msg, "error")} />}
      {editingUser && <EditUserModal user={editingUser} tenantId={tenantId} onUpdated={() => { setEditingUser(null); fetchUsers(); showToast(t.users.successUpdated, "success"); }} onClose={() => setEditingUser(null)} onError={(msg) => showToast(msg, "error")} />}
      {toast && <div role="alert" style={{ position: "fixed", bottom: "24px", right: "24px", background: toast.type === "success" ? "#111" : colors.error, color: "#fff", fontFamily: typography.fontFamily, fontSize: typography.size.sm, padding: "12px 20px", borderRadius: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 300 }}>{toast.message}</div>}
    </div>
  );
});

function CreateUserModal({ tenantId, onCreated, onClose, onError }: { tenantId: string; onCreated: () => void; onClose: () => void; onError: (msg: string) => void }) {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [roleName, setRoleName] = useState("");
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { listRoles(tenantId).then(setRoles).catch(() => {}); }, [tenantId]);
  useEffect(() => { const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; document.addEventListener("keydown", h); return () => document.removeEventListener("keydown", h); }, [onClose]);

  const isValid = name.trim().length >= 2 && username.trim().length >= 2 && email.includes("@") && password.length >= 8 && /[a-z]/.test(password) && /[A-Z]/.test(password) && /\d/.test(password) && /[^a-zA-Z0-9]/.test(password) && !!roleName;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setSaving(true);
    try {
      await createUser({ name: name.trim(), username: username.trim(), email: email.trim(), password, tenantId, roleName });
      onCreated();
    } catch (err: any) {
      const msg = err?.response?.status === 409 ? t.users.errorDuplicate : t.users.errorValidation;
      onError(msg);
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", padding: "32px", width: "100%", maxWidth: "460px", display: "flex", flexDirection: "column", gap: "16px" }}>
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{t.users.addUser}</h2>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <Field label={t.users.nameLabel} value={name} onChange={setName} placeholder={t.users.namePlaceholder} required />
          <Field label={t.users.usernameLabel} value={username} onChange={setUsername} placeholder={t.users.usernamePlaceholder} required />
          <Field label={t.users.emailLabel} value={email} onChange={setEmail} placeholder={t.users.emailPlaceholder} type="email" required />
          <PasswordField label={t.users.passwordLabel} value={password} onChange={setPassword} placeholder={t.users.passwordPlaceholder} required />
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{t.users.roleLabel} *</label>
            <select value={roleName} onChange={e => setRoleName(e.target.value)} style={inputStyle as any}>
              <option value="">{t.users.rolePlaceholder}</option>
              {roles.map(r => <option key={r.id} value={r.name}>{r.name.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "4px" }}>
            <button type="button" onClick={onClose} style={btnSecondary}>{t.users.cancel}</button>
            <button type="submit" disabled={!isValid || saving} style={isValid && !saving ? btnPrimary : btnDisabled}>{t.users.save}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({ user, tenantId, onUpdated, onClose, onError }: { user: UserRecord; tenantId: string; onUpdated: () => void; onClose: () => void; onError: (msg: string) => void }) {
  const { t } = useI18n();
  const [name, setName] = useState(user.name);
  const [username, setUsername] = useState(user.username);
  const [email, setEmail] = useState(user.email);
  const [password, setPassword] = useState("");
  const [roleName, setRoleName] = useState(user.roleName);
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { listRoles(tenantId).then(setRoles).catch(() => {}); }, [tenantId]);
  useEffect(() => { const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; document.addEventListener("keydown", h); return () => document.removeEventListener("keydown", h); }, [onClose]);

  const passwordValid = password.length === 0 || (password.length >= 8 && /[a-z]/.test(password) && /[A-Z]/.test(password) && /\d/.test(password) && /[^a-zA-Z0-9]/.test(password));
  const isValid = name.trim().length >= 2 && username.trim().length >= 2 && email.includes("@") && passwordValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setSaving(true);
    try {
      // Update basic user data
      const data: any = {};
      if (name.trim() !== user.name) data.name = name.trim();
      if (username.trim() !== user.username) data.username = username.trim();
      if (email.trim() !== user.email) data.email = email.trim();
      if (password.length > 0) data.password = password;
      if (Object.keys(data).length > 0) {
        await updateUser(user.id, data);
      }
      // Update role if changed
      if (roleName !== user.roleName) {
        const selectedRole = roles.find(r => r.name === roleName);
        if (selectedRole) {
          await updateUserRole(user.id, tenantId, selectedRole.id);
        }
      }
      onUpdated();
    } catch (err: any) {
      const msg = err?.response?.status === 409 ? t.users.errorDuplicate : t.users.errorValidation;
      onError(msg);
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", padding: "32px", width: "100%", maxWidth: "460px", display: "flex", flexDirection: "column", gap: "16px" }}>
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{t.users.editUser}</h2>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <Field label={t.users.nameLabel} value={name} onChange={setName} placeholder={t.users.namePlaceholder} required />
          <Field label={t.users.usernameLabel} value={username} onChange={setUsername} placeholder={t.users.usernamePlaceholder} required />
          <Field label={t.users.emailLabel} value={email} onChange={setEmail} placeholder={t.users.emailPlaceholder} type="email" required />
          <PasswordField label={`${t.users.passwordLabel} (${t.users.passwordOptional})`} value={password} onChange={setPassword} placeholder={t.users.passwordNewPlaceholder} />
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{t.users.roleLabel}</label>
            <select value={roleName} onChange={e => setRoleName(e.target.value)} style={inputStyle as any}>
              {roles.length === 0 && <option value={user.roleName}>{user.roleName.replace(/_/g, " ")}</option>}
              {roles.map(r => <option key={r.id} value={r.name}>{r.name.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "4px" }}>
            <button type="button" onClick={onClose} style={btnSecondary}>{t.users.cancel}</button>
            <button type="submit" disabled={!isValid || saving} style={isValid && !saving ? btnPrimary : btnDisabled}>{t.users.save}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", required = false }: { label: string; value: string; onChange: (v: string) => void; placeholder: string; type?: string; required?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{label}{required && " *"}</label>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} type={type} style={inputStyle} />
    </div>
  );
}

function PasswordField({ label, value, onChange, placeholder, required = false }: { label: string; value: string; onChange: (v: string) => void; placeholder: string; required?: boolean }) {
  const { t } = useI18n();
  const [show, setShow] = useState(false);
  const hasMin = value.length >= 8;
  const hasLower = /[a-z]/.test(value);
  const hasUpper = /[A-Z]/.test(value);
  const hasNumber = /\d/.test(value);
  const hasSpecial = /[^a-zA-Z0-9]/.test(value);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{label}{required && " *"}</label>
      <div style={{ position: "relative" }}>
        <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} type={show ? "text" : "password"} style={{ ...inputStyle, paddingRight: "40px" }} />
        <button type="button" onClick={() => setShow(v => !v)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#aaa", display: "flex", padding: 0 }}>
          {show ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
          )}
        </button>
      </div>
      {value.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "3px", marginTop: "2px" }}>
          <PwRule pass={hasMin} label={t.users.pwMin8 ?? "Minimum 8 characters"} />
          <PwRule pass={hasLower} label={t.users.pwLower ?? "At least one lowercase letter"} />
          <PwRule pass={hasUpper} label={t.users.pwUpper ?? "At least one uppercase letter"} />
          <PwRule pass={hasNumber} label={t.users.pwNumber ?? "At least one number"} />
          <PwRule pass={hasSpecial} label={t.users.pwSpecial ?? "At least one special character"} />
        </div>
      )}
    </div>
  );
}

function PwRule({ pass, label }: { pass: boolean; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <span style={{ fontSize: "12px", color: pass ? "#16a34a" : "#bbb" }}>{pass ? "✓" : "✗"}</span>
      <span style={{ fontFamily: typography.fontFamily, fontSize: "12px", color: pass ? "#16a34a" : "#999" }}>{label}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const isActive = status === "ACTIVE";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: isActive ? "rgba(34,197,94,0.1)" : "rgba(156,163,175,0.15)", color: isActive ? "#16a34a" : "#6b7280", fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, padding: "4px 10px", borderRadius: "9999px" }}>
      <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: isActive ? "#16a34a" : "#6b7280" }} />
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

function RowBtn({ label, onClick, danger = false }: { label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button onClick={onClick} style={{ fontFamily: typography.fontFamily, fontSize: "12px", fontWeight: typography.weight.semibold, color: danger ? colors.error : "#666", background: "transparent", border: `1px solid ${danger ? "rgba(239,68,68,0.3)" : "#e8e8e8"}`, borderRadius: "7px", padding: "6px 12px", cursor: "pointer", transition: `all ${transitions.fast}`, whiteSpace: "nowrap" }}>
      {label}
    </button>
  );
}

const cell: React.CSSProperties = { padding: "14px 16px", verticalAlign: "middle" };
const inputStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box" };
const btnPrimary: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: "#111", border: "none", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" };
const btnSecondary: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" };
const btnDisabled: React.CSSProperties = { ...btnPrimary, background: "#ccc", cursor: "not-allowed" };
